import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, Users, Send, Mail, QrCode, Settings as SettingsIcon, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import TrialBanner from "@/components/TrialBanner";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/send", label: "Send request", icon: Send, testid: "nav-send" },
  { to: "/template", label: "Email template", icon: Mail, testid: "nav-template" },
  { to: "/qr", label: "QR code", icon: QrCode, testid: "nav-qr" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const access = user?.access;

  const handleLogout = () => { logout(); navigate("/"); };

  const NavItems = ({ onClick }) => {
    const isLocked = !access?.access;
    return (
      <nav className="flex flex-col gap-1 px-3">
        {nav.map(({ to, label, icon: Icon, testid }) => {
          const disabled = isLocked && to !== "/settings";
          return (
            <NavLink
              key={to}
              to={disabled ? "/settings" : to}
              data-testid={testid}
              onClick={(e) => {
                if (disabled) {
                  e.preventDefault();
                  navigate("/settings");
                }
                onClick && onClick();
              }}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium transition-colors ${
                  isActive && !disabled ? "bg-primary text-primary-foreground" : disabled ? "text-slate-400 cursor-not-allowed" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <Icon className="h-5 w-5" strokeWidth={2.2} />
              {label}
              {disabled && to !== "/settings" && <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">Locked</span>}
            </NavLink>
          );
        })}
      </nav>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/dashboard" className="font-display font-bold text-lg text-primary" data-testid="mobile-logo">Job Done</Link>
          <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen((v) => !v)} className="p-2 rounded-lg hover:bg-slate-100">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {mobileOpen && (
          <div className="bg-white border-b border-slate-200 py-3">
            <NavItems onClick={() => setMobileOpen(false)} />
            <div className="px-3 mt-3">
              <Button data-testid="mobile-logout-btn" variant="outline" className="w-full rounded-xl" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" /> Log out
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden md:flex md:flex-col w-64 min-h-screen bg-white border-r border-slate-200 sticky top-0">
          <div className="px-6 py-6">
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
              <div className="h-9 w-9 rounded-xl bg-primary text-white grid place-items-center font-display font-bold">J</div>
              <div className="font-display font-bold text-lg leading-tight">Job Done<br/><span className="text-slate-500 text-xs font-medium">Reviews</span></div>
            </Link>
          </div>
          <div className="flex-1">
            <NavItems />
          </div>
          <div className="p-3 border-t border-slate-200">
            <div className="px-3 py-2 text-xs text-slate-500 truncate" data-testid="sidebar-user-email">{user?.email}</div>
            <Button data-testid="sidebar-logout-btn" variant="ghost" className="w-full justify-start rounded-xl" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" /> Log out
            </Button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0">
          {access?.banner && <TrialBanner access={access} />}
          <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
